import { ProcessUrlResponse, processUrlResponse } from '../types';
import { SiteClassification, WebDomainRules } from '../types/api/types';
import { ReportSiteAction, ReportSiteReason } from '../types/api/events';
import { RulesStore } from '../types/store';

export const getFilteredCategories = (classification: SiteClassification) => {
  return new Set(
    classification.manual_only
      ? classification.manual_categories
      : [
          ...classification.manual_categories,
          ...classification.engine_categories,
        ]
  );
};

export const validateDomain = (
  rules: RulesStore,
  classification?: SiteClassification
): {
  categories: number[];
  categories_match: number[];
  action: number;
  reason: number;
  verdict: ProcessUrlResponse;
} => {
  // If there is no classification, ALLOW navigation.
  if (!classification) {
    return NO_MATCH_NO_CATEGORIES;
  }

  // Site unclassified and unknown sites allowed: ALLOW navigation.
  // Site unclassified and unknown sites NOT allowed: BLOCK navigation.
  const filteredCategories = getFilteredCategories(classification);
  if (isUnclassifiedSite(filteredCategories)) {
    return rules.allowUnknownSites
      ? NO_MATCH_NO_CATEGORIES
      : {
          categories: [],
          categories_match: [],
          action: ReportSiteAction.BLOCK,
          reason: ReportSiteReason.NOT_ALLOW_UNKNOWN_SITES,
          verdict: processUrlResponse.block(),
        };
  }

  const blockRules = rules.isCategoryList
    ? rules.categories.filter(
        rule => rule.action === 'BLOCK' && filteredCategories.has(rule.category)
      )
    : [];
  const categories = Array.from(filteredCategories) as number[];
  const categories_match = blockRules.map(r => r.category);
  const matchesBlockedCategories = blockRules.length === 0;

  return matchesBlockedCategories
    ? {
        categories,
        categories_match,
        action: ReportSiteAction.ALLOW,
        reason: ReportSiteReason.NO_MATCH,
        verdict: processUrlResponse.allow(),
      }
    : {
        categories,
        categories_match,
        action: ReportSiteAction.BLOCK,
        reason: ReportSiteReason.CATEGORY_BLOCK,
        verdict: processUrlResponse.block(),
      };
};

const NO_MATCH_NO_CATEGORIES = {
  categories: [],
  categories_match: [],
  action: ReportSiteAction.ALLOW,
  reason: ReportSiteReason.NO_MATCH,
  verdict: processUrlResponse.allow(),
};

const isUnclassifiedSite = (categories: Set<number>) => {
  // If there are no categories or the only category is 0, then the site is NOT classified.
  const [first, ...rest] = categories;
  return rest.length === 0 && !first;
};

export const getWhitelistVerdict = (
  rules: WebDomainRules,
  domain: string
): { verdict: ProcessUrlResponse; action: ReportSiteAction } | undefined => {
  if (!Object.keys(rules).includes(domain)) {
    return;
  }

  if (rules[domain] === ReportSiteAction.BLOCK) {
    return {
      action: ReportSiteAction.BLOCK,
      verdict: processUrlResponse.block(),
    };
  }

  return {
    action: rules[domain],
    verdict: processUrlResponse.allow(),
  };
};
